import React, { useEffect } from 'react';
import styles from './variantSelection.scss';
import { inject, observer } from 'mobx-react';
import { IVariantStore, VariantType } from '../../../types';
import classNames from 'classnames';
import TabNavigationHelper from '../../../helpers/tabNavigationHelper';
import VariantButton from '../brickItemsPanel/variantButtonList/variantButton/variantButton';
import Cross from '../../icons/cross';
import { ColorPanel } from './colorPanel/colorPanel';
import { SizePanel } from './sizePanel/sizePanel';
import { SpecialPanel } from './specialPanel/specialPanel';
import { ModelPanel } from './modelPanel/modelPanel';
import Apply from '../../icons/apply';

const VariantSelection = inject('variantStore')(
    observer(({ variantStore }: IVariantStore) => {
        const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;
        const variantSelectionPanelRef = React.createRef<HTMLDivElement>();

        useEffect(() => {
            if (variantSelectionPanelRef.current) {
                if (variantStore?.isVariantSelectionPanelOpen === true) {
                    const firstFocusable = variantSelectionPanelRef.current.querySelectorAll('[focusable]')[0];
                    window.shell.tabNav.focus(firstFocusable);
                }
            }
        }, [variantStore?.isVariantSelectionPanelOpen]);

        const colorButton = () => {
            if (!variantStore?.selectedBrickItem) {
                return <></>
            }
            if (variantStore?.sbiVariantSelection?.showColorSwitcher)
                return <VariantButton
                    variantType={VariantType.Color}
                    isMobileHeader={true}
                    brickItem={variantStore?.selectedBrickItem} />;
            else {
                if (variantStore.selectedBrickItem.variantSelection.availableColors[0]?.code !== 0)
                    return <VariantButton variantType={VariantType.Color}
                        brickItem={variantStore.selectedBrickItem}
                        isMobileHeader={true} isDeactive={true} />;
                else return <></>;
            }
        };

        const modelButton = () => {
            if (!variantStore?.selectedBrickItem) {
                return <></>;
            }
            if (variantStore?.sbiVariantSelection?.showModelSwitcher)
                return <VariantButton variantType={VariantType.Model} isMobileHeader={true}
                    brickItem={variantStore?.selectedBrickItem} />;
            else if (variantStore?.sbiVariantSelection?.availableModels[0]?.code !== 0)
                return <VariantButton variantType={VariantType.Model}
                    brickItem={variantStore?.selectedBrickItem}
                    isMobileHeader={true} isDeactive={true} />;
            else return <></>;
        };

        const closeClick = () => {
            if (variantStore?.isVariantSelectionPanelOpen) {
                variantStore.changeVariantSelectionPanelDisplay(false);
            }
        };

        const desktopHeader = () => {
            return (
                <>
                    <div role='close_button' className={
                        classNames(styles.close_button, 'variant_selection_close_button')}
                        onClick={closeClick}
                        ref={(el) => tabNavigation.setFocusAtt(el)}>
                        <Cross />
                    </div>
                </>
            );
        };

        const revertSelectionAndClosePanel = () => {
            variantStore?.resetVariantState();
            if (variantStore?.isVariantSelectionPanelOpen)
                variantStore.changeVariantSelectionPanelDisplay(false);
        };

        const applyChange = () => {
            if (variantStore?.isVariantSelectionPanelOpen)
                variantStore.changeVariantSelectionPanelDisplay(false);
        };

        return (
            <div
                role='panel'
                id='variant_selection'
                className={classNames(
                    styles.variant_selection,
                    {
                        // For test reason to use pure string
                        ['fsp-show']: variantStore?.isVariantSelectionPanelOpen,
                    }
                )}
                ref={(el) => tabNavigation.setVariantPanelAttributes(el,
                    variantStore?.isVariantSelectionPanelOpen)}
            >
                <div role='header' className={classNames(styles.header, styles.desktop_h)}>
                    {(variantStore?.variantType === VariantType.Color ||
                        variantStore?.variantType === VariantType.Size ||
                        variantStore?.variantType === VariantType.Special ||
                        variantStore?.variantType === VariantType.Model) &&
                        desktopHeader()}

                </div>
                <div role='mobile_header' className={classNames(styles.header, styles.mobile_h)}>
                    {colorButton()}
                    {variantStore?.selectedBrickItem.variantSelection.showSizeSwitcher
                        && !variantStore.selectedBrickItem.variantSelection.isScrew && (
                            <VariantButton
                                variantType={VariantType.Size}
                                isMobileHeader={true}
                                brickItem={variantStore?.selectedBrickItem} />
                        )}
                    {variantStore?.selectedBrickItem.variantSelection?.showSizeSwitcher
                        && variantStore.selectedBrickItem.variantSelection.isScrew && (
                            <VariantButton
                                variantType={VariantType.Special}
                                isMobileHeader={true}
                                brickItem={variantStore?.selectedBrickItem} />
                        )}
                    {modelButton()}
                </div>

                <div role='content' className={styles.content} ref={variantSelectionPanelRef}>
                    {variantStore?.variantType === VariantType.Color && <ColorPanel />}
                    {variantStore?.variantType === VariantType.Size && <SizePanel />}
                    {variantStore?.variantType === VariantType.Special && <SpecialPanel />}
                    {variantStore?.variantType === VariantType.Model && <ModelPanel />}
                </div>
                <div className={styles.footer_gap_ghost}></div>
                <div role='footer' className={classNames(styles.footer, styles.mobile_f)}>
                    <div
                        className={classNames(styles.button, styles.cancel, 'variant_selection_close_button')}
                        data-testid='footer-cancel-button' data-semantic-id='footer-cancel-button'
                        onClick={revertSelectionAndClosePanel}
                        ref={(el) => tabNavigation.setFocusAtt(el)}
                        onKeyDown={tabNavigation.handleKeyDown}
                    >
                        <Cross />
                        <div className={styles.button_title}>
                            {variantStore?.mainStore.localization.generalL10n.buttonClose}
                        </div>
                    </div>
                    <div
                        className={classNames(styles.button, styles.apply, 'variant_selection_apply_button')}
                        onClick={applyChange}
                        data-testid='flexset-button-layer_2-confirm' data-semantic-id='flexset-button-layer_2-confirm'
                        data-flexset-id={variantStore?.selectedBrickItem.fsCode}
                        data-flexset-name={variantStore?.mainStore.dataStore.orderModule.flexSetInfo.displayName}
                        data-flexset-module-layer_0-category={variantStore?.getOMBrickSelection?.mainNavigationKeyPath}
                        data-flexset-module-layer_0-index={variantStore?.getOMBrickPosition}
                        data-flexset-module-layer_1-category={variantStore?.selectedBrickItem.mainNavigationKeyPath}
                        data-flexset-module-layer_1-index={variantStore?.brickItemPosition}
                        data-flexset-item-name={variantStore?.selectedBrickItem.salesDesignation}
                        data-flexset-item-sku={variantStore?.mainStore.
                          getSalesArticleNo(variantStore?.selectedBrickItem.selectedSavKey)}
                        ref={(el) => tabNavigation.setFocusAtt(el)}
                    >
                        <Apply />
                        <div className={styles.button_title}>
                            {variantStore?.mainStore.localization.generalL10n.buttonApply}
                        </div>
                    </div>
                </div>
            </div>
        );
    }));

export default VariantSelection;

